// Visit Wizard Type Definitions
// Multi-step workflow for creating visits in PAO system

import type { Client } from './client'

// Re-export Client for convenience
export type { Client } from './client'

// Wizard step enumeration
export type WizardStep =
  | 'client-selection'
  | 'client-review'
  | 'classification'
  | 'denial'
  | 'assistance-selection'
  | 'assistance-form'
  | 'summary'
  | 'confirmation'

// Assistance type enumeration
export type AssistanceType =
  | 'legal-counselling'
  | 'documentation'
  | 'oaths-administered'
  | 'jail-visit'
  | 'representation-court'
  | 'representation-appellate'
  | 'prosecutor-office'
  | 'labor-cases'
  | 'administrative-case'
  | 'outreach'
  | 'mediation-conciliation'

// Client classification categories
export type ClientClassification =
  | 'Senior Citizen'
  | 'Pregnant Women'
  | 'VAWC (RA 9262) Victim'
  | 'Child in Conflict with the Law'
  | 'Women'
  | 'Rural Poor'
  | 'Urban Poor'
  | 'Foreign National'
  | 'PWD - Vision'
  | 'PWD - Hearing'
  | 'PWD - Speech'
  | 'PWD - Physical Disability'
  | 'OFW – Land-based'
  | 'OFW – Sea-based'
  | 'Refugee/Evacuee'
  | 'Taiwanese National'
  | 'Tenant in Agrarian Case'
  | 'Petitioner for Voluntary Rehabilitation (Drugs)'
  | 'Arrested for Terrorism (R.A. No. 11479)'
  | 'Victim of Trafficking (R.A. No. 9208)'
  | 'Victim of Torture (R.A. No. 9745)'
  | 'Former Rebel (FR) and Former Violent Extremist'
  | 'Law Enforcer'

// Classification data collected in step 3
export interface Classification {
  client_classifications: ClientClassification[] // Multiple classifications can apply to a client
  decision: 'assist' | 'deny' // Determines routing after classification
}

// Assistance instance in the workflow
export interface Assistance {
  id: string // Temporary ID for UI tracking
  type: AssistanceType
  control_number?: string // Generated by backend after save
  data: Record<string, any> // Form data specific to assistance type
  is_saved: boolean // Whether it's been saved to backend
}

// Denial reason data
export interface DenialReason {
  reasons: Array<{
    option: string
    reason?: string
  }>
  timestamp: string
}

// Complete wizard state
export interface VisitWizardState {
  // Wizard control
  currentStep: WizardStep
  stepHistory: WizardStep[]
  completedSteps: Set<WizardStep>

  // Data
  selectedClient: Client | null
  classification: Classification | null
  denialReason: DenialReason | null
  assistances: Assistance[]
  currentAssistanceType: AssistanceType | null
  visitId: string | null // Created after classification
  existingControlNumbers: string[] // For duplicate prevention

  // UI state
  isSubmitting: boolean
  errors: Record<string, string>
}

// Wizard actions for reducer
export type WizardAction =
  | { type: 'SET_STEP'; payload: WizardStep }
  | { type: 'GO_BACK' }
  | { type: 'SELECT_CLIENT'; payload: Client }
  | { type: 'SET_CLASSIFICATION'; payload: Classification }
  | { type: 'SET_DENIAL_REASON'; payload: DenialReason }
  | { type: 'SELECT_ASSISTANCE_TYPE'; payload: AssistanceType }
  | { type: 'ADD_ASSISTANCE'; payload: Assistance }
  | { type: 'UPDATE_ASSISTANCE'; payload: { id: string; data: Partial<Assistance> } }
  | { type: 'REMOVE_ASSISTANCE'; payload: string }
  | { type: 'SET_VISIT_ID'; payload: string }
  | { type: 'SET_EXISTING_CONTROL_NUMBERS'; payload: string[] }
  | { type: 'SET_ERROR'; payload: { field: string; message: string } }
  | { type: 'CLEAR_ERRORS' }
  | { type: 'SET_SUBMITTING'; payload: boolean }
  | { type: 'RESET_WIZARD' }

// Assistance type metadata for UI
export const ASSISTANCE_TYPE_LABELS: Record<AssistanceType, string> = {
  'legal-counselling': 'Legal Counselling',
  'documentation': 'Documentation',
  'oaths-administered': 'Oaths Administered',
  'jail-visit': 'Jail Visit',
  'representation-court': 'Representation in Court/Quasi-Judicial Bodies',
  'representation-appellate': 'Representation before the Appellate Court',
  'prosecutor-office': "Prosecutor's Office",
  'labor-cases': 'Labor Cases',
  'administrative-case': 'Administrative Case Proper',
  'outreach': 'Legal Information and Dissemination Outreach',
  'mediation-conciliation': 'Mediation and Conciliation',
}

// Step labels for progress display
export const STEP_LABELS: Record<WizardStep, string> = {
  'client-selection': 'Select Client',
  'client-review': 'Review/Edit Client',
  'classification': 'Classification',
  'denial': 'Denial Reason',
  'assistance-selection': 'Select Assistance',
  'assistance-form': 'Assistance Form',
  'summary': 'Summary',
  'confirmation': 'Confirmation',
}

// Client classification options organized by category
export const GENERAL_CLASSIFICATIONS: ClientClassification[] = [
  'Senior Citizen',
  'Pregnant Women',
  'VAWC (RA 9262) Victim',
  'Child in Conflict with the Law',
  'Women',
  'Rural Poor',
  'Urban Poor',
  'Foreign National',
]

export const PWD_CLASSIFICATIONS: ClientClassification[] = [
  'PWD - Vision',
  'PWD - Hearing',
  'PWD - Speech',
  'PWD - Physical Disability',
]

export const OTHER_CLASSIFICATIONS: ClientClassification[] = [
  'OFW – Land-based',
  'OFW – Sea-based',
  'Refugee/Evacuee',
  'Taiwanese National',
  'Tenant in Agrarian Case',
  'Petitioner for Voluntary Rehabilitation (Drugs)',
  'Arrested for Terrorism (R.A. No. 11479)',
  'Victim of Trafficking (R.A. No. 9208)',
  'Victim of Torture (R.A. No. 9745)',
  'Former Rebel (FR) and Former Violent Extremist',
  'Law Enforcer',
]
